import api from './api.service';

const TENANT_CONFIG_CACHE_KEY = 'tenantConfig';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Convert snake_case keys to camelCase
 */
const toCamelCase = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);

  return Object.keys(obj).reduce((acc, key) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    acc[camelKey] = toCamelCase(obj[key]);
    return acc;
  }, {});
};

/**
 * Extract subdomain from current hostname
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;

  // Handle localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check for subdomain in localStorage for testing
    return localStorage.getItem('testSubdomain') || 'doc';
  }

  // Extract subdomain from hostname (e.g., "type01.thaklispro.com" -> "type01")
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const subdomain = parts[0].toLowerCase();
    // Skip www
    if (subdomain === 'www') {
      return parts.length >= 3 ? parts[1].toLowerCase() : 'doc';
    }
    return subdomain;
  }

  return 'doc'; // Default to platform
};

/**
 * Fetch tenant configuration from API
 */
export const fetchTenantConfig = async (subdomain = null) => {
  const resolvedSubdomain = subdomain || getSubdomain();

  // Check cache first
  const cached = getCachedConfig(resolvedSubdomain);
  if (cached) {
    return cached;
  }

  try {
    const response = await api.get(`/api/public/tenant/config`, {
      params: { subdomain: resolvedSubdomain }
    });

    // Convert snake_case to camelCase
    const config = toCamelCase(response.data);

    // Cache the result
    if (config.valid) {
      setCachedConfig(resolvedSubdomain, config);
    }

    return config;
  } catch (error) {
    console.error('Failed to fetch tenant config:', error);
    return {
      valid: false,
      errorMessage: 'Failed to load tenant configuration'
    };
  }
};

/**
 * Check if subdomain is available
 */
export const checkSubdomainAvailability = async (subdomain) => {
  try {
    const response = await api.get(`/api/public/tenant/check-subdomain`, {
      params: { subdomain }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to check subdomain:', error);
    return { available: false, error: error.message };
  }
};

/**
 * Get domains for a typing center (admin)
 */
export const getTenantDomains = async (typingCenterId) => {
  const response = await api.get(`/api/tenant/domains/${typingCenterId}`);
  return response.data;
};

/**
 * Create a new domain mapping (admin)
 */
export const createTenantDomain = async (domainData) => {
  const response = await api.post('/api/tenant/domains', domainData);
  return response.data;
};

/**
 * Delete a domain mapping (admin)
 */
export const deleteTenantDomain = async (domainId) => {
  await api.delete(`/api/tenant/domains/${domainId}`);
};

/**
 * Update theme configuration (admin)
 */
export const updateTenantTheme = async (typingCenterId, themeData) => {
  const response = await api.put(`/api/tenant/theme/${typingCenterId}`, themeData);
  return response.data;
};

/**
 * Get current authenticated tenant config
 */
export const getCurrentTenantConfig = async () => {
  const response = await api.get('/api/tenant/config');
  return toCamelCase(response.data);
};

// Cache helpers
const getCachedConfig = (subdomain) => {
  try {
    const cached = sessionStorage.getItem(`${TENANT_CONFIG_CACHE_KEY}_${subdomain}`);
    if (!cached) return null;

    const { config, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(`${TENANT_CONFIG_CACHE_KEY}_${subdomain}`);
      return null;
    }

    return config;
  } catch {
    return null;
  }
};

const setCachedConfig = (subdomain, config) => {
  try {
    sessionStorage.setItem(
      `${TENANT_CONFIG_CACHE_KEY}_${subdomain}`,
      JSON.stringify({ config, timestamp: Date.now() })
    );
  } catch {
    // Ignore storage errors
  }
};

/**
 * Clear tenant config cache
 */
export const clearTenantCache = () => {
  const keys = Object.keys(sessionStorage);
  keys.forEach(key => {
    if (key.startsWith(TENANT_CONFIG_CACHE_KEY)) {
      sessionStorage.removeItem(key);
    }
  });
};

export default {
  getSubdomain,
  fetchTenantConfig,
  checkSubdomainAvailability,
  getTenantDomains,
  createTenantDomain,
  deleteTenantDomain,
  updateTenantTheme,
  getCurrentTenantConfig,
  clearTenantCache
};
